import { IonIcon, IonProgressBar } from '@ionic/react';
import {
  checkmarkCircle,
  chevronForward,
  closeCircle,
  warning,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';

import ui from '../../offline-ui.module.css';
import { computeSyncProgress, SyncJob } from '../../sync-job/types';

export type SyncStatusStripProps = {
  job: SyncJob;
  isRunning: boolean;
  onOpenDetails?: () => void;
  onResume?: () => void;
  variant?: 'banner' | 'inline';
};

export function SyncStatusStrip({
  job,
  isRunning,
  onOpenDetails,
  onResume,
  variant = 'banner',
}: SyncStatusStripProps) {
  const { t } = useTranslation(['common'], { useSuspense: false });
  const progress = computeSyncProgress(job);

  const plotsValue =
    progress.totalPlots > 0
      ? progress.completedPlots / progress.totalPlots
      : 0;

  if (job.status === 'paused') {
    return (
      <div
        className={`${ui.syncStrip} ${ui.syncStripPaused}`}
        data-variant={variant}
      >
        <div className={ui.stripMain}>
          <p className={ui.stripTitle}>{t('common:sync_job.banner_paused')}</p>
          <IonProgressBar className={ui.stripBar} value={plotsValue} />
        </div>
        {onResume ? (
          <button type="button" className={ui.stripAction} onClick={onResume}>
            {t('common:sync_job.resume')}
          </button>
        ) : null}
      </div>
    );
  }

  if (
    job.status === 'completed' ||
    job.status === 'completed_with_errors' ||
    job.status === 'failed'
  ) {
    const isSuccess = job.status === 'completed';
    const isPartial = job.status === 'completed_with_errors';
    const stripClass = isSuccess
      ? ui.syncStripSuccess
      : isPartial
        ? ui.syncStripWarning
        : ui.syncStripDanger;

    const title = isSuccess
      ? t('common:sync_job.completed_title')
      : isPartial
        ? t('common:sync_job.completed_partial_title')
        : t('common:sync_job.failed_title');

    const summary = isSuccess
      ? t('common:sync_job.completed_summary', {
          plots: job.totals.plots,
          analyses: job.totals.analyses,
        })
      : isPartial
        ? t('common:sync_job.completed_with_errors', {
            count: job.errors.length,
          })
        : t('common:sync_job.failed');

    const content = (
      <>
        <IonIcon
          className={ui.completionIcon}
          icon={
            isSuccess
              ? checkmarkCircle
              : isPartial
                ? warning
                : closeCircle
          }
          style={{ fontSize: 28, flexShrink: 0 }}
        />
        <div className={ui.stripMain}>
          <p className={ui.stripTitle}>{title}</p>
          <p className={ui.stripHint} style={{ margin: 0, opacity: 1 }}>
            {summary}
          </p>
          {onOpenDetails ? (
            <p className={ui.stripHint}>{t('common:sync_job.banner_done_tap')}</p>
          ) : null}
        </div>
        {onOpenDetails ? (
          <IonIcon className={ui.stripChevron} icon={chevronForward} />
        ) : null}
      </>
    );

    if (onOpenDetails) {
      return (
        <button
          type="button"
          className={`${ui.syncStrip} ${ui.syncStripInteractive} ${stripClass}`}
          data-variant={variant}
          onClick={onOpenDetails}
        >
          {content}
        </button>
      );
    }

    return (
      <div
        className={`${ui.syncStrip} ${stripClass}`}
        data-variant={variant}
      >
        {content}
      </div>
    );
  }

  const label = t('common:sync_job.banner', {
    percent: progress.plotsPercent,
    remaining: progress.remainingPlots,
  });

  const inner = (
    <>
      <div className={ui.stripMain}>
        <p className={ui.stripTitle}>{label}</p>
        <IonProgressBar className={ui.stripBar} value={plotsValue} />
        {onOpenDetails ? (
          <p className={ui.stripHint}>
            {t('common:sync_job.banner_tap')}
          </p>
        ) : null}
      </div>
      {onOpenDetails ? (
        <IonIcon className={ui.stripChevron} icon={chevronForward} />
      ) : null}
    </>
  );

  if (onOpenDetails) {
    return (
      <button
        type="button"
        className={`${ui.syncStrip} ${ui.syncStripInteractive} ${ui.syncStripRunning}`}
        data-variant={variant}
        onClick={onOpenDetails}
      >
        {inner}
      </button>
    );
  }

  return (
    <div
      className={`${ui.syncStrip} ${ui.syncStripRunning}`}
      data-variant={variant}
    >
      {inner}
    </div>
  );
}

export default SyncStatusStrip;
